import type {
  FormField,
  ReactiveDropdownDataSource,
  ReactiveDropdownDataSourceContext,
  ReactiveDropdownKeySelector,
} from '../../interfaces/form.interface';
import { DropdownData, Value } from '../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';

export type ReactiveDropdownOptionData = Value<unknown> & Record<string, unknown>;

export class ReactiveDropdownMapper<T> {

  public mapResponse(params: {
    readonly field: FormField<T>;
    readonly response: unknown;
    readonly context: ReactiveDropdownDataSourceContext<T>;
    readonly currentData: DropdownData<unknown>;
  }): DropdownData<unknown> {
    const dataSource = params.field.dataSource;
    const mappedResponse = dataSource?.mapResponse
      ? dataSource.mapResponse(params.response, params.context)
      : this.pickResponsePayload(params.response, dataSource?.responsePath);

    if (this.isDropdownData(mappedResponse)) {
      const values = this.normalizeOptions(params.field, mappedResponse.values, params.context);
      const filtered = this.normalizeOptions(
        params.field,
        mappedResponse.filtered?.length ? mappedResponse.filtered : values,
        params.context,
      );

      return {
        values,
        filtered,
        label: mappedResponse.label ?? params.currentData.label ?? '',
      };
    }

    const values = this.normalizeOptions(params.field, mappedResponse, params.context);

    return {
      values,
      filtered: values,
      label: params.currentData.label ?? '',
    };
  }

  public isDropdownData(value: unknown): value is DropdownData<unknown> {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const record = value as Partial<DropdownData<unknown>>;

    return Array.isArray(record.values) && Array.isArray(record.filtered);
  }

  private normalizeOptions(
    field: FormField<T>,
    items: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
  ): ReactiveDropdownOptionData[] {
    const values = Array.isArray(items) ? items : [];
    const mappedOptions = values.map((item) => this.mapOption(field, item, context));

    return this.deduplicateOptions(field, mappedOptions, context);
  }

  private mapOption(field: FormField<T>, item: unknown, context: ReactiveDropdownDataSourceContext<T>): ReactiveDropdownOptionData {
    const dataSource = field.dataSource;

    if (dataSource?.mapOption) {
      return dataSource.mapOption(item, context) as ReactiveDropdownOptionData;
    }

    if (item !== null && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const hasNativeOptionShape = 'value' in record && 'label' in record;
      const label = this.resolveLabel(field, record);
      const value = this.resolveValue(dataSource, record, item, hasNativeOptionShape, context);

      return {
        ...this.pickOptionMetadata(record),
        label,
        value,
        raw: record['raw'] ?? item,
      };
    }

    return {
      label: String(item ?? ''),
      value: item,
    };
  }

  private resolveLabel(field: FormField<T>, record: Record<string, unknown>): string {
    const dataSource = field.dataSource;
    const labelKeys = [
      dataSource?.labelKey,
      ...(dataSource?.labelKeys ?? []),
      field.input?.optionLabel,
      'label',
      'nombre',
      'name',
      'razon_social',
      'descripcion',
      'codigo',
      'id',
    ].filter((key): key is string => typeof key === 'string' && key.trim().length > 0);

    for (const key of labelKeys) {
      const value = record[key];

      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value);
      }
    }

    return '';
  }

  private resolveValue(
    dataSource: ReactiveDropdownDataSource<T> | undefined,
    record: Record<string, unknown>,
    item: unknown,
    hasNativeOptionShape: boolean,
    context: ReactiveDropdownDataSourceContext<T>,
  ): unknown {
    const value = this.readFirstNonEmptyRecordValue(record, [
      dataSource?.valueKey,
      ...(dataSource?.valueKeys ?? []),
    ]);

    if (value !== null) {
      return value;
    }

    if (dataSource?.fallbackValue) {
      const fallbackValue = dataSource.fallbackValue(item, context);

      if (ReactiveFormValueUtils.hasNonEmptyValue(fallbackValue)) {
        return fallbackValue;
      }
    }

    if (hasNativeOptionShape) {
      return record['value'];
    }

    return item;
  }

  private readFirstNonEmptyRecordValue(record: Record<string, unknown>, keys: readonly (string | undefined)[]): unknown | null {
    for (const key of keys) {
      if (!key || !(key in record)) {
        continue;
      }

      const value = record[key];

      if (ReactiveFormValueUtils.hasNonEmptyValue(value)) {
        return value;
      }
    }

    return null;
  }

  private deduplicateOptions(
    field: FormField<T>,
    options: ReactiveDropdownOptionData[],
    context: ReactiveDropdownDataSourceContext<T>,
  ): ReactiveDropdownOptionData[] {
    const distinctBy = field.dataSource?.distinctBy;

    if (!distinctBy) {
      return options;
    }

    const seen = new Set<string>();
    const result: ReactiveDropdownOptionData[] = [];

    for (const option of options) {
      const key = this.normalizeDistinctKey(this.resolveDistinctKey(distinctBy, option, context));

      if (key === '' || seen.has(key)) {
        continue;
      }

      seen.add(key);
      result.push(option);
    }

    return result;
  }

  private resolveDistinctKey(
    distinctBy: ReactiveDropdownKeySelector<T>,
    option: ReactiveDropdownOptionData,
    context: ReactiveDropdownDataSourceContext<T>,
  ): unknown {
    const rawValue = option.raw ?? option.value ?? option;

    if (typeof distinctBy === 'function') {
      return distinctBy(rawValue, context);
    }

    const record = rawValue !== null && typeof rawValue === 'object'
      ? rawValue as Record<string, unknown>
      : option as Record<string, unknown>;

    if (Array.isArray(distinctBy)) {
      return distinctBy.map((key) => record[key]).join(':');
    }

    return record[distinctBy];
  }

  private normalizeDistinctKey(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  private pickOptionMetadata(record: Record<string, unknown>): Record<string, unknown> {
    const metadata: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(record)) {
      if (key === 'value' || key === 'label') {
        continue;
      }

      if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
        metadata[key] = value;
      }
    }

    return metadata;
  }

  private pickResponsePayload(response: unknown, responsePath: string | undefined): unknown {
    if (responsePath) {
      return this.readResponsePath(response, responsePath);
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response !== null && typeof response === 'object') {
      const record = response as Record<string, unknown>;

      if (Array.isArray(record['data'])) {
        return record['data'];
      }

      if (Array.isArray(record['values'])) {
        return record['values'];
      }

      if (Array.isArray(record['items'])) {
        return record['items'];
      }

      if (Array.isArray(record['result'])) {
        return record['result'];
      }
    }

    return [];
  }

  private readResponsePath(response: unknown, responsePath: string): unknown {
    return responsePath
      .split('.')
      .reduce<unknown>((current, key) => {
        if (current !== null && typeof current === 'object') {
          return (current as Record<string, unknown>)[key];
        }

        return undefined;
      }, response);
  }
}
